import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Input, Progress, Spin, Tooltip } from 'antd';
import { CloseOutlined, MessageOutlined, PaperClipOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import http from '../../store/services/http-common';
import useTranslation from '../../localization/useTranslation';
import appToast from '../../utilities/appToast';
import { getStoredUser } from '../../utilities/authentication';
import { fetchSupportThread } from '../../query/fetchers/support';
import { queryKeys } from '../../query/queryKeys';
import { ORDER_ATTACHMENT_ACCEPT } from '../../constants/allowedAttachments';
import SupportChatAttachments, { attachmentIcon, formatBytes, supportFileProblem } from './SupportChatAttachments';
import './SupportChatWidget.css';

const THREAD_POLL_MS = 5000;
const MAX_BODY_LENGTH = 2000;
/** Keep in sync with backend SupportChatService::MAX_FILES_PER_MESSAGE */
const MAX_FILES_PER_MESSAGE = 5;

let pendingFileSeq = 0;

/**
 * @param {{ variant?: 'floating' | 'page', onClose?: () => void }} props
 */
const SupportChatPanel = ({ variant = 'floating', onClose }) => {
  const { t, language } = useTranslation();
  const queryClient = useQueryClient();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [dragging, setDragging] = useState(false);
  const lastIdRef = useRef(0);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const isPage = variant === 'page';

  const mergeMessages = useCallback((incoming) => {
    if (!incoming?.length) return;
    setMessages((prev) => {
      const known = new Set(prev.map((m) => m.id));
      const fresh = incoming.filter((m) => !known.has(m.id));
      return fresh.length ? [...prev, ...fresh].sort((a, b) => a.id - b.id) : prev;
    });
  }, []);

  const applyThread = useCallback((thread) => {
    const incoming = thread?.messages ?? [];
    setConversation(thread?.conversation ?? null);
    mergeMessages(incoming);
    if (incoming.length) {
      lastIdRef.current = Math.max(lastIdRef.current, ...incoming.map((m) => m.id));
    }
    queryClient.setQueryData(queryKeys.support.unreadCount(), 0);
  }, [mergeMessages, queryClient]);

  useEffect(() => {
    let cancelled = false;
    const load = () => fetchSupportThread(lastIdRef.current)
      .then((thread) => { if (!cancelled) applyThread(thread); })
      .catch(() => {});

    setLoading(lastIdRef.current === 0);
    load().finally(() => { if (!cancelled) setLoading(false); });
    const timer = setInterval(() => {
      if (!document.hidden) load();
    }, THREAD_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [applyThread]);

  useEffect(() => {
    if (!isPage) {
      inputRef.current?.focus();
    }
  }, [isPage]);

  useEffect(() => {
    if (!isPage || typeof window === 'undefined') {
      return undefined;
    }

    const rootStyle = document.documentElement.style;
    const setViewportHeight = () => {
      const height = window.visualViewport?.height || window.innerHeight;
      rootStyle.setProperty('--support-chat-vh', `${Math.round(height)}px`);
    };

    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    window.visualViewport?.addEventListener('resize', setViewportHeight);

    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.visualViewport?.removeEventListener('resize', setViewportHeight);
      rootStyle.removeProperty('--support-chat-vh');
    };
  }, [isPage]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages.length, loading]);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList ?? []);
    if (!incoming.length) return;
    const accepted = incoming.filter((file) => {
      const problem = supportFileProblem(file);
      if (problem) {
        appToast.error(t(`support.chat.files.${problem}`, { name: file.name }));
        return false;
      }
      return true;
    });
    const room = Math.max(0, MAX_FILES_PER_MESSAGE - pendingFiles.length);
    if (accepted.length > room) {
      appToast.error(t('support.chat.files.tooMany', { count: MAX_FILES_PER_MESSAGE }));
    }
    const added = accepted.slice(0, room).map((file) => {
      pendingFileSeq += 1;
      return { key: `support-file-${pendingFileSeq}`, file };
    });
    if (added.length) setPendingFiles((prev) => [...prev, ...added]);
  };

  const removePendingFile = (key) => setPendingFiles((prev) => prev.filter((p) => p.key !== key));

  const canSend = Boolean(draft.trim()) || pendingFiles.length > 0;

  const send = async () => {
    const body = draft.trim();
    if (!canSend || sending) return;
    setSending(true);
    try {
      let payload = { body };
      let requestConfig;
      if (pendingFiles.length) {
        payload = new FormData();
        if (body) payload.append('body', body);
        pendingFiles.forEach(({ file }) => payload.append('files[]', file));
        setUploadProgress(0);
        requestConfig = {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (event) => {
            if (event.total) setUploadProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)));
          },
        };
      }
      const { data } = await http.post('/support/messages', payload, requestConfig);
      mergeMessages([data?.data?.message].filter(Boolean));
      setConversation(data?.data?.conversation ?? null);
      setDraft('');
      setPendingFiles([]);
    } catch (error) {
      appToast.apiError(error, t('support.chat.sendError'));
    } finally {
      setSending(false);
      setUploadProgress(null);
      inputRef.current?.focus();
    }
  };

  const isFileDrag = (event) => Array.from(event.dataTransfer?.types ?? []).includes('Files');
  const dropHandlers = {
    onDragOver: (event) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      if (!dragging) setDragging(true);
    },
    onDragLeave: (event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false);
    },
    onDrop: (event) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      setDragging(false);
      if (!sending) addFiles(event.dataTransfer.files);
    },
  };

  const onPaste = (event) => {
    if (event.clipboardData?.files?.length) {
      event.preventDefault();
      if (!sending) addFiles(event.clipboardData.files);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      send();
    }
  };

  const firstName = useMemo(() => {
    const name = String(getStoredUser()?.name ?? '').trim();
    return name.split(/\s+/)[0] || '';
  }, []);

  const lastSeenMineId = useMemo(() => {
    const readAt = conversation?.support_last_read_at ? dayjs(conversation.support_last_read_at) : null;
    if (!readAt) return null;
    const seen = messages.filter((m) => m.sender === 'me' && !dayjs(m.created_at).isAfter(readAt));
    return seen.length ? seen[seen.length - 1].id : null;
  }, [conversation?.support_last_read_at, messages]);

  const isRtl = language === 'ar';

  return (
    <section
      className={`support-chat__panel${isPage ? ' support-chat__panel--page' : ''}${dragging ? ' support-chat__panel--dragging' : ''}`}
      role={isPage ? 'region' : 'dialog'}
      aria-label={t('support.chat.title')}
      dir={isRtl ? 'rtl' : 'ltr'}
      {...dropHandlers}
    >
      {dragging ? (
        <div className="support-chat__drop-overlay">
          <PaperClipOutlined />
          <span>{t('support.chat.files.dropHere')}</span>
        </div>
      ) : null}
      <header className="support-chat__header">
        <span className="support-chat__header-icon" aria-hidden="true">
          <MessageOutlined />
        </span>
        <div className="support-chat__header-text">
          <strong>{t('support.chat.title')}</strong>
          <span>{t('support.chat.subtitle')}</span>
        </div>
        {onClose ? (
          <Button
            type="text"
            className="support-chat__close"
            aria-label={t('support.chat.close')}
            icon={<CloseOutlined />}
            onClick={onClose}
          />
        ) : null}
      </header>

      <div className="support-chat__messages" ref={listRef}>
        {loading ? (
          <div className="support-chat__loading"><Spin /></div>
        ) : (
          <>
            <div className="support-chat__bubble-row support-chat__bubble-row--support">
              <div className="support-chat__bubble support-chat__bubble--support">
                {firstName
                  ? t('support.chat.greetingNamed', { name: firstName })
                  : t('support.chat.greeting')}
              </div>
            </div>
            {messages.map((m) => {
              const mine = m.sender === 'me';
              return (
                <div
                  key={m.id}
                  className={`support-chat__bubble-row support-chat__bubble-row--${mine ? 'mine' : 'support'}`}
                >
                  {!mine && m.author_name ? (
                    <span className="support-chat__author">{m.author_name}</span>
                  ) : null}
                  {m.body ? (
                    <div className={`support-chat__bubble support-chat__bubble--${mine ? 'mine' : 'support'}`}>
                      {m.body}
                    </div>
                  ) : null}
                  <SupportChatAttachments attachments={m.attachments} />
                  <span className="support-chat__meta">
                    {dayjs(m.created_at).format('DD MMM, HH:mm')}
                    {m.id === lastSeenMineId ? ` · ${t('support.chat.seen')}` : ''}
                  </span>
                </div>
              );
            })}
          </>
        )}
      </div>

      {pendingFiles.length ? (
        <div className="support-chat__pending">
          {pendingFiles.map(({ key, file }) => (
            <span key={key} className="support-chat__pending-file" title={file.name}>
              <span className="support-chat__file-icon">
                {attachmentIcon({ name: file.name, type: String(file.type).split('/')[0] })}
              </span>
              <span className="support-chat__file-name">{file.name}</span>
              <span className="support-chat__file-size">{formatBytes(file.size)}</span>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                disabled={sending}
                onClick={() => removePendingFile(key)}
                aria-label={t('support.chat.files.remove')}
              />
            </span>
          ))}
          {uploadProgress !== null ? (
            <Progress
              percent={uploadProgress}
              size="small"
              status="active"
              format={(percent) => (percent >= 99 ? t('support.chat.files.processing') : `${percent}%`)}
            />
          ) : null}
        </div>
      ) : null}

      <footer className="support-chat__composer">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          accept={ORDER_ATTACHMENT_ACCEPT}
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <Tooltip title={t('support.chat.files.attach')}>
          <Button
            className="support-chat__attach"
            icon={<PaperClipOutlined />}
            aria-label={t('support.chat.files.attach')}
            disabled={sending || pendingFiles.length >= MAX_FILES_PER_MESSAGE}
            onClick={() => fileInputRef.current?.click()}
          />
        </Tooltip>
        <Input.TextArea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          placeholder={t('support.chat.placeholder')}
          autoSize={{ minRows: 1, maxRows: 5 }}
          maxLength={MAX_BODY_LENGTH}
          disabled={sending}
        />
        <Button
          type="primary"
          icon={<SendOutlined style={isRtl ? { transform: 'scaleX(-1)' } : undefined} />}
          aria-label={t('support.chat.send')}
          loading={sending}
          disabled={!canSend}
          onClick={send}
        >
          {t('support.chat.send')}
        </Button>
      </footer>
    </section>
  );
};

export default SupportChatPanel;
